import {Button} from '@components/ui/button'
import {AlertTriangleIcon} from '@components/ui/icon'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {useDeclineAssignment} from '@features/assignment/queries/useDeclineAssignment'
import {Assignment, ServiceType} from '@features/assignment/types.ts'
import {Spinner} from '@components/ui/spinner'
import {useDeclineReasons} from '@features/assignment/queries/useDeclineReasons'
import {ErrorBox} from '@components/commons/error-box'
import {Radio, RadioGroup} from '@components/commons/radio'
import {Controller, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {TextArea} from '@components/commons/textarea'
import {ModalBody, ModalCloseButton, ModalFooter, ModalHeader, ModalTitle} from '@components/ui/modal-atoms'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'
import {noUnsupportedChars} from '@utilities/helpers'

const ValidationSchema = z.object({
    reason_id: z.coerce.number().int().positive(),
    notes: noUnsupportedChars(z.string())
})
type ValidationSchema = z.infer<typeof ValidationSchema>

export const DeclineAssignmentModal = ({
    assignment,
    onClose,
    onSuccess,
    serviceType
}: {
    assignment: Assignment
    onSuccess?: () => void
    onClose: () => void
    serviceType: ServiceType
}) => {
    const {t} = useTranslation()
    const declineAssignmentMutation = useDeclineAssignment(assignment, {onSuccess})
    const declineReasonsQuery = useDeclineReasons()
    const form = useForm<ValidationSchema>({
        mode: 'onChange',
        resolver: zodResolver(ValidationSchema)
    })

    const onSubmit = form.handleSubmit(values => {
        declineAssignmentMutation.mutate(values)
    })

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent
                className="w-120 grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
                showCloseButton={false}
            >
                <ModalHeader>
                    <DialogClose
                        data-slot="dialog-close"
                        render={<ModalCloseButton disabled={declineAssignmentMutation.isPending} />}
                    />
                </ModalHeader>
                <ModalBody>
                    {declineReasonsQuery.isPending ? (
                        <Spinner size={32} className="self-center" />
                    ) : declineReasonsQuery.isError ? (
                        <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
                    ) : (
                        <>
                            <div className="flex flex-col gap-2 [&>p]:text-sm">
                                <ModalTitle>{t('assignment:decline:title')}</ModalTitle>
                                <p>{t(`assignment:decline:description:${serviceType}` as TKey)}</p>
                            </div>

                            <Controller
                                control={form.control}
                                name="reason_id"
                                render={({field: {onChange, value}}) => (
                                    <RadioGroup
                                        onValueChange={(value: string) => onChange(value)}
                                        value={value !== undefined ? value : ''}
                                        className="flex flex-col gap-2"
                                    >
                                        {declineReasonsQuery.data.map(reason => (
                                            <Radio
                                                label={t(
                                                    `single_shooting:reasons:decline:${reason?.key_name.replace('reasons.', '')}` as TKey
                                                )}
                                                value={reason.id}
                                                id={reason.id.toString()}
                                                key={reason.id}
                                            />
                                        ))}
                                    </RadioGroup>
                                )}
                            />
                            <TextArea
                                {...form.register('notes')}
                                label={t('commons:notes')}
                                errorMessage={t((form.formState.errors?.notes?.message ?? '') as TKey)}
                            />
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="tertiary" disabled={declineAssignmentMutation.isPending}>
                        {t('commons:close')}
                    </Button>
                    <Button
                        type="submit"
                        disabled={declineAssignmentMutation.isPending || !form.formState.isValid}
                        onClick={onSubmit}
                    >
                        {t('assignment:decline:cta')}
                        {declineAssignmentMutation.isPending && <Spinner size={20} />}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
